import { Box, Button, CircularProgress } from '@mui/material';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import Modal from '~/components/Modal';
import { useDicomExport } from '~/hooks/export/useDicomExport';
import useExportSelections from '~/hooks/export/useExportSelection';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import {
    enableExportMonacoDirFileState,
    exportDatasetsModalOpenState,
} from '~/state/atoms/viewerAtoms';
import ExportConfirmation from './ExportConfirmation';
import ExportHeader from './ExportHeader';
import ExportableItemsList from './ExportItemSelection';

const ExportDatasetModal = () => {
    const [open, setOpen] = useRecoilState(exportDatasetsModalOpenState);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const { exporting, handleDownloadBatch, handleExportBatch } =
        useDicomExport();
    const enableExportMonacoDirFile = useRecoilValue(
        enableExportMonacoDirFileState
    );
    const patient = useSelectedPatient();
    const exportableImages =
        patient?.images.filter((image) => image.isNew || image.isExported) ??
        [];
    const exportableStructureSets =
        patient?.structureSets.filter((ss) => ss.isNew || ss.isExported) ?? [];
    const exportableDoses =
        patient?.doses.filter((dose) => dose.isNew || dose.isExported) ?? [];

    const {
        selectedImages,
        selectedStructureSets,
        selectedDoses,
        structureSelections,
        handleSelectImage,
        handleSelectStructureSet,
        handleSelectStructure,
        handleSelectDose,
        resetSelections,
    } = useExportSelections(exportableStructureSets, exportableDoses, open);

    const hasSelection =
        selectedImages.length > 0 ||
        selectedStructureSets.length > 0 ||
        selectedDoses.length > 0;

    const handleExport = async () => {
        setConfirmationDialogOpen(false);
        await handleExportBatch(
            selectedImages,
            selectedStructureSets.map((ss) => ({
                structureSet: ss,
                selectedRois: structureSelections[ss.dicomUid],
            })),
            selectedDoses
        );
        resetSelections();
    };

    const defaultExpanded =
        exportableImages?.length > 0
            ? exportableImages[0].dicomUid
            : exportableStructureSets?.length > 0
              ? exportableStructureSets[0].dicomUid
              : exportableDoses?.length > 0
                ? exportableDoses[0].dicomUid
                : '';

    return (
        <Modal open={open} onClose={() => setOpen(false)}>
            <Box display="grid" gap={2} minWidth={600}>
                <ExportHeader />
                <ExportConfirmation
                    open={confirmationDialogOpen}
                    setOpen={setConfirmationDialogOpen}
                    onConfirm={handleExport}
                    selectedDoses={selectedDoses}
                    selectedStructureSets={selectedStructureSets}
                    structureSelections={structureSelections}
                />
                <Box height="100%">
                    <ExportableItemsList
                        exportableImages={exportableImages}
                        exportableDoses={exportableDoses}
                        exportableStructureSets={exportableStructureSets}
                        selectedImages={selectedImages}
                        selectedStructureSets={selectedStructureSets}
                        selectedDoses={selectedDoses}
                        structureSelections={structureSelections}
                        handleSelectImage={handleSelectImage}
                        handleSelectStructureSet={handleSelectStructureSet}
                        handleSelectStructure={handleSelectStructure}
                        handleSelectDose={handleSelectDose}
                        defaultExpanded={defaultExpanded}
                    />

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                        mt={2}
                    >
                        <Button
                            data-testid="downloadButton"
                            variant="text"
                            color="primary"
                            onClick={() =>
                                handleDownloadBatch(
                                    selectedImages,
                                    selectedStructureSets.map((ss) => ({
                                        structureSet: ss,
                                        selectedRois:
                                            structureSelections[ss.dicomUid],
                                    })),
                                    selectedDoses,
                                    enableExportMonacoDirFile
                                )
                            }
                            disabled={exporting || !hasSelection}
                            sx={{ width: 120 }}
                            startIcon={
                                exporting ? (
                                    <CircularProgress size={12} />
                                ) : (
                                    <Download size={12} />
                                )
                            }
                        >
                            <FormattedMessage id="download" />
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setConfirmationDialogOpen(true)}
                            disabled={exporting || !hasSelection}
                            sx={{ width: 120 }}
                        >
                            <FormattedMessage id="export" />
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default ExportDatasetModal;
